/**
 * Estado de uma execução a partir dos eventos — a MESMA regra de
 * `pharuerp-backend/area_pcp/projecao.py` (ADR-0087, decisão 6), para o
 * celular avançar sem internet. `casos_projecao.json` é o contrato entre os
 * dois lados: os dois testes passam pelos mesmos casos.
 *
 * 1. Lê em ordem de `ocorrido_em`; empate por `recebido_em` (o que ainda não
 *    chegou ao servidor fica por último) e depois pela posição na lista.
 * 2. Em cada passo vale o último registro (feito, desfeito ou pulado).
 * 3. Pausar põe em pausa; retomar ou mexer num passo tira.
 * 4. Cancelar encerra: nada depois conta.
 * 5. Todos os passos feitos ou pulados: conclui ali, nada depois conta.
 * 6. Conflito: no passo, o registro que chegou por último perdeu para um
 *    anterior de outro aparelho com efeito diferente.
 */

export const TIPO = Object.freeze({
  FEITO: "feito",
  DESFEITO: "desfeito",
  PULADO: "pulado",
  PAUSOU: "pausou",
  RETOMOU: "retomou",
  CANCELOU: "cancelou",
});

export const STATUS = Object.freeze({
  EM_ANDAMENTO: "em_andamento",
  PAUSADA: "pausada",
  CONCLUIDA: "concluida",
  CANCELADA: "cancelada",
});

const TIPOS_DE_PASSO = new Set([TIPO.FEITO, TIPO.DESFEITO, TIPO.PULADO]);

/** Unidades executáveis na ordem: cada passo, ou cada item do sub-checklist. */
export function unidadesDoRoteiro(roteiro) {
  const unidades = [];
  (roteiro?.passos || []).forEach((passo, indice) => {
    const itens = passo.itens || [];
    if (itens.length) {
      itens.forEach((item) => unidades.push({ chave: item.chave, passo: passo.chave, indice_passo: indice }));
    } else {
      unidades.push({ chave: passo.chave, passo: passo.chave, indice_passo: indice });
    }
  });
  return unidades;
}

function ms(valor) {
  if (valor === null || valor === undefined || valor === "") return Infinity;
  return Date.parse(valor);
}

function ordenar(eventos) {
  return eventos
    .map((ev, posicao) => ({ ocorrido: ms(ev.ocorrido_em), recebido: ms(ev.recebido_em), posicao, ev }))
    .sort((a, b) => a.ocorrido - b.ocorrido || a.recebido - b.recebido || a.posicao - b.posicao);
}

function conflitos(chaves, ordenados) {
  const porUnidade = new Map();
  ordenados.forEach((marca) => {
    if (!TIPOS_DE_PASSO.has(marca.ev.tipo)) return;
    if (!porUnidade.has(marca.ev.unidade)) porUnidade.set(marca.ev.unidade, []);
    porUnidade.get(marca.ev.unidade).push(marca);
  });
  const comConflito = new Set();
  porUnidade.forEach((marcas, unidade) => {
    if (new Set(marcas.map((m) => m.ev.dispositivo || "")).size < 2) return;
    const vencedor = marcas[marcas.length - 1];
    const ultimoAChegar = marcas.reduce((maior, m) =>
      m.recebido > maior.recebido || (m.recebido === maior.recebido && m.posicao > maior.posicao) ? m : maior,
    );
    if (ultimoAChegar !== vencedor && ultimoAChegar.ev.tipo !== vencedor.ev.tipo) comConflito.add(unidade);
  });
  return chaves.filter((chave) => comConflito.has(chave));
}

/**
 * `chaves`: unidades na ordem do roteiro. `eventos`: `{tipo, unidade,
 * ocorrido_em, recebido_em (null se ainda não chegou), dispositivo, motivo, usuario}`.
 */
export function projetar(chaves, eventos) {
  const validas = new Set(chaves);
  const ordenados = ordenar(eventos || []);
  const estado = {};
  let status = STATUS.EM_ANDAMENTO;
  let motivoPausa = "";
  let motivoCancelamento = "";
  let concluidaEm = null;
  let canceladaEm = null;

  for (const { ev } of ordenados) {
    const { tipo } = ev;
    if (tipo === TIPO.CANCELOU) {
      status = STATUS.CANCELADA;
      motivoCancelamento = ev.motivo || "";
      canceladaEm = ev.ocorrido_em;
      break;
    }
    if (tipo === TIPO.PAUSOU) {
      status = STATUS.PAUSADA;
      motivoPausa = ev.motivo || "";
      continue;
    }
    if (tipo === TIPO.RETOMOU) {
      status = STATUS.EM_ANDAMENTO;
      motivoPausa = "";
      continue;
    }
    if (!TIPOS_DE_PASSO.has(tipo) || !validas.has(ev.unidade)) continue;
    status = STATUS.EM_ANDAMENTO;
    motivoPausa = "";
    if (tipo === TIPO.DESFEITO) {
      delete estado[ev.unidade];
    } else {
      estado[ev.unidade] = { estado: tipo, em: ev.ocorrido_em, por: ev.usuario || "" };
    }
    if (chaves.length && Object.keys(estado).length === chaves.length) {
      status = STATUS.CONCLUIDA;
      concluidaEm = ev.ocorrido_em;
      break;
    }
  }

  return {
    status,
    motivo_pausa: status === STATUS.PAUSADA ? motivoPausa : "",
    motivo_cancelamento: motivoCancelamento,
    unidades: estado,
    atual: chaves.find((chave) => !estado[chave]) ?? null,
    concluidos: Object.keys(estado).length,
    total: chaves.length,
    concluida_em: concluidaEm,
    cancelada_em: canceladaEm,
    conflitos: conflitos(chaves, ordenados),
  };
}
